import { useState, useEffect } from 'react';
import { useAuth } from '../context/FirebaseAuthContext';
import {
  getUserData,
  spendUserCredits,
  getUserCreditTransactions,
  hasEnoughCredits,
  UserData,
  CreditTransaction
} from '../services/firebaseService';
import { hasAnyActiveSubscription } from '../utils/subscriptionStatus';
import { hasActiveSubscriptionByEmail } from '../utils/supabase-subscription-checker';

// Define UserEntitlement type locally
interface UserEntitlement {
  hasActiveSubscription: boolean;
  subscriptionId?: string;
  plan?: string;
}

interface UseUserCreditsReturn {
  userData: UserData | null;
  creditTransactions: CreditTransaction[];
  loading: boolean;
  error: string | null;
  refreshUserData: () => Promise<void>;
  spendCredits: (amount: number, action: string) => Promise<boolean>;
  checkCredits: (amount: number) => Promise<boolean>;
  hasActiveSubscription: boolean;
  entitlement: UserEntitlement | null;
}

export const useUserCredits = (): UseUserCreditsReturn => {
  const { user: currentUser } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [entitlement, setEntitlement] = useState<UserEntitlement | null>(null);

  const refreshUserData = async () => {
    if (!currentUser) {
      setUserData(null);
      setCreditTransactions([]);
      setEntitlement(null);
      setHasActiveSubscription(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check Supabase subscription for authenticated users
      const hasSupabaseSubscription = await hasActiveSubscriptionByEmail(currentUser.email || '');
      const userEntitlement: UserEntitlement = {
        hasActiveSubscription: hasSupabaseSubscription,
        subscriptionId: hasSupabaseSubscription ? 'supabase_subscription' : undefined,
        plan: hasSupabaseSubscription ? 'premium' : 'free'
      };
      setEntitlement(userEntitlement);
      setHasActiveSubscription(hasSupabaseSubscription);

      // Fetch user data
      const userDataResult = await getUserData(currentUser.uid);
      setUserData(userDataResult);

      // Try to fetch credit transactions, but don't fail if it doesn't work
      try {
        const transactionsResult = await getUserCreditTransactions(currentUser.uid, 20);
        setCreditTransactions(transactionsResult);
      } catch (transactionError) {
        console.warn('Could not load credit transactions:', transactionError);
        setCreditTransactions([]); // Set empty array if transactions fail
      }

    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const spendCredits = async (amount: number, action: string): Promise<boolean> => {
    if (!currentUser) {
      setError('User not authenticated');
      return false;
    }

    try {
      const success = await spendUserCredits(currentUser.uid, amount, action);
      if (success) {
        // Refresh user data after spending credits
        await refreshUserData();
      }
      return success;
    } catch (err) {
      console.error('Error spending credits:', err);
      setError('Failed to spend credits');
      return false;
    }
  };

  const checkCredits = async (amount: number): Promise<boolean> => {
    try {
      // If user is authenticated, check Supabase subscription first
      if (currentUser?.email) {
        const hasSupabaseSubscription = await hasActiveSubscriptionByEmail(currentUser.email);
        if (hasSupabaseSubscription) {
          console.log('User has active Supabase subscription - unlimited credits');
          return true;
        }

        // Check Firebase credits for authenticated users without subscription
        return await hasEnoughCredits(currentUser.uid, amount);
      }

      // For guest users, check guest subscription status
      const hasSubscription = await hasAnyActiveSubscription();
      if (hasSubscription) {
        // Guest with active subscription has unlimited credits
        return true;
      }

      // Guest without subscription cannot perform actions
      return false;
    } catch (err) {
      console.error('Error checking credits:', err);
      return false;
    }
  };

  // Check subscription status on mount and when user changes
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const hasSubscription = await hasAnyActiveSubscription();
        setHasActiveSubscription(hasSubscription);
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setHasActiveSubscription(false);
      }
    };

    refreshUserData();
    checkSubscriptionStatus();
  }, [currentUser]);

  return {
    userData,
    creditTransactions,
    loading,
    error,
    refreshUserData,
    spendCredits,
    checkCredits,
    hasActiveSubscription,
    entitlement
  };
};
